import { Form, message } from 'antd';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { mapPackToUpdateRequest } from 'app/hooks/data/core/admin/packs/mapping/form';
import { useCreatePack } from 'app/hooks/data/core/admin/packs/useCreatePack';
import { useDeletePack } from 'app/hooks/data/core/admin/packs/useDeletePack';
import { useUpdatePack } from 'app/hooks/data/core/admin/packs/useUpdatePack';
import { combineDateAndTime, zonedWallClockToUtcIso } from 'app/utils/dates';
import { isHttpErrorBody } from 'app/utils/error';
import { SCROLL_TO_FIRST_ERROR } from 'app/utils/scroll';

import type { MenuItemName } from '../menuItem';
import {
  activeTabHasFieldError,
  getFirstActiveTabErrorField,
  getRejectedFields,
} from '../packTabFields';

import type { PackInitialFormType } from './usePackData';

type UsePackActionsProps = {
  packId?: number;
  isCreatePage: boolean;
  initialValues?: Partial<PackInitialFormType>;
  isFetching?: boolean;
  activeTab: MenuItemName;
  hotelTimezone?: string;
};

const toSubmittedValues = (
  values: Partial<PackInitialFormType>,
  hotelTimezone?: string
) => {
  const { launchDate, launchTime, ...restValues } = values;

  return {
    ...restValues,
    launchDate:
      launchDate && hotelTimezone
        ? zonedWallClockToUtcIso(
            combineDateAndTime(launchDate, launchTime),
            hotelTimezone
          )
        : null,
  };
};

export const usePackActions = ({
  packId,
  isCreatePage,
  initialValues,
  isFetching,
  activeTab,
  hotelTimezone,
}: UsePackActionsProps) => {
  const [form] = Form.useForm<PackInitialFormType>();
  const history = useHistory();

  const { mode, enterViewMode, enterEditMode } =
    useContext(ViewEditModeContext);

  const { mutateAsync: createPack, isPending: isCreating } = useCreatePack();
  const { mutateAsync: updatePack, isPending: isUpdating } = useUpdatePack();
  const { mutateAsync: deletePack } = useDeletePack();

  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (isCreatePage) {
      enterEditMode();
    }
  }, [isCreatePage, enterEditMode]);

  // Re-sync the form from the server only when the loaded pack changes identity
  // (e.g. after a create → /packs/:id redirect, which reuses the same route
  // without remounting) or while the user hasn't touched anything yet. Guarding
  // on isFieldsTouched() alone never resynced after a create, because antd keeps
  // fields "touched" after a successful submit.
  const lastSyncedPackId = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (
      packId &&
      !isFetching &&
      initialValues &&
      (initialValues.id !== lastSyncedPackId.current || !form.isFieldsTouched())
    ) {
      lastSyncedPackId.current = initialValues.id;
      form.resetFields();
    }
  }, [packId, isFetching, initialValues, form]);

  const onFinish = useCallback(
    async (values: PackInitialFormType) => {
      const handleError = (error: unknown) => {
        const cause = error instanceof Error ? error.cause : undefined;
        const errorMessage = isHttpErrorBody(cause)
          ? (cause as Record<string, unknown>).message
          : error instanceof Error
            ? error.message
            : 'An unknown error occurred';

        message.error(`Package save failed: ${errorMessage}`, 15);
      };

      const submittedValues = toSubmittedValues(values, hotelTimezone);

      if (isCreatePage) {
        await createPack(submittedValues, {
          onSuccess: (createdPack) => {
            history.push({ pathname: `/packs/${createdPack?.id}` });
            enterViewMode();
          },
          onError: handleError,
        });
      } else if (packId) {
        const payload = mapPackToUpdateRequest(
          submittedValues,
          activeTab,
          initialValues
            ? toSubmittedValues(initialValues, hotelTimezone)
            : undefined
        );

        if (Object.keys(payload).length === 0) {
          enterViewMode();

          return;
        }

        await updatePack(
          { id: packId, payload },
          {
            onSuccess: () => {
              enterViewMode();
            },
            onError: handleError,
          }
        );
      }
    },
    [
      isCreatePage,
      packId,
      createPack,
      history,
      enterViewMode,
      activeTab,
      initialValues,
      updatePack,
      hotelTimezone,
    ]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    enterViewMode();
  }, [form, enterViewMode]);

  const handleDelete = useCallback(async () => {
    if (packId) {
      await deletePack(packId);
      history.push('/packs');
    }
  }, [deletePack, packId, history]);

  const saveActiveTab = useCallback(
    async (onSaved?: () => void): Promise<'saved' | 'invalid' | 'failed'> => {
      try {
        await form.validateFields();
      } catch (error) {
        const rejectedFields = getRejectedFields(error);

        const blocks = isCreatePage
          ? true
          : rejectedFields.length === 0 ||
            activeTabHasFieldError(rejectedFields, activeTab);

        if (blocks) {
          setCanSubmit(false);

          const firstErrorField = isCreatePage
            ? rejectedFields[0]?.name
            : getFirstActiveTabErrorField(rejectedFields, activeTab);

          if (firstErrorField !== undefined) {
            form.scrollToField(firstErrorField, SCROLL_TO_FIRST_ERROR);
          }

          return 'invalid';
        }
      }

      try {
        await onFinish(form.getFieldsValue());
        onSaved?.();

        return 'saved';
      } catch (error) {
        console.error('Error saving package:', error);

        return 'failed';
      }
    },
    [form, isCreatePage, activeTab, onFinish]
  );

  const handleSave = useCallback(() => {
    void saveActiveTab();
  }, [saveActiveTab]);

  return {
    form,
    mode,
    canSubmit,
    isLoading,
    onCancel,
    handleDelete,
    handleSave,
    saveActiveTab,
    enterEditMode,
    setCanSubmit,
    enterViewMode,
  };
};
